import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { chatApi } from '../api/chat'

/**
 * Кнопка «Чат с родителем» в карточке ученика для учителя.
 *
 * Логика:
 *  1. По клику запрашивает список peer'ов (родителей привязанных к этому ученику)
 *  2. Если их 0 — сообщает что родитель ещё не зарегистрировался по invite
 *  3. Если 1+ — рендерит выбор, по клику открывает thread и редиректит в /app/chat
 */
export function ChatWithParentButton({ studentId }: { studentId: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const peers = useMutation({ mutationFn: () => chatApi.peersForStudent(studentId) })
  const open = useMutation({
    mutationFn: (peerId: string) => chatApi.openThread(peerId, studentId),
    onSuccess: ({ threadId }) => navigate(`/app/chat?thread=${threadId}`),
  })

  if (!peers.data) {
    return (
      <div className="mt-2 mb-3">
        <button
          type="button"
          onClick={() => peers.mutate()}
          disabled={peers.isPending}
          className="text-sm text-primary hover:underline"
        >
          {peers.isPending ? t('chatWithParentButton.searchingParents') : `💬 ${t('chatWithParentButton.chatWithParent')}`}
        </button>
      </div>
    )
  }

  if (peers.data.length === 0) {
    return (
      <div className="mt-2 mb-3 text-xs text-gray-500">
        💬 {t('chatWithParentButton.parentNotRegistered')}
      </div>
    )
  }

  return (
    <div className="mt-2 mb-3 bg-primary-soft/30 border border-primary/20 rounded-lg p-3">
      <div className="font-semibold text-sm mb-2">💬 {t('chatWithParentButton.chatWithParent')}</div>
      {peers.data.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => open.mutate(p.id)}
          disabled={open.isPending}
          className="block w-full text-left px-3 py-2 text-sm rounded hover:bg-white transition-colors"
        >
          {p.name}
          <span className="text-xs text-gray-500 ml-1">
            ({p.role.replace('parent:', '') || t('chatWithParentButton.relationDefault')})
          </span>
        </button>
      ))}
    </div>
  )
}
